import type { AwsBody, AwsS3Options } from '@uppy/aws-s3';
import type { UppyFile } from '@uppy/core';
import { uploadS3ControllerPresign } from '@/api';

export type UppyMeta = Record<string, unknown>;

type AwsS3SignOptions = Extract<AwsS3Options<UppyMeta, AwsBody>, { signRequest: unknown }>;
type SignRequestFn = AwsS3SignOptions['signRequest'];
type PresignableRequest = Parameters<SignRequestFn>[0];

/**
 * Uppy 6 dropped Companion mode's custom headers and talks to S3 directly, so
 * every request is presigned by us instead. The presign call goes through the
 * generated SDK, which carries the session bearer and the 401 refresh-retry
 * interceptor - that is why the upload plugins no longer set any headers.
 */
export const signS3Request: SignRequestFn = async (request: PresignableRequest) => {
  const { data, status } = await uploadS3ControllerPresign({
    method: request.method,
    key: request.key,
    uploadId: 'uploadId' in request ? request.uploadId : undefined,
    partNumber: 'partNumber' in request ? request.partNumber : undefined,
    expiresIn: request.expiresIn,
  });

  if (status !== 201) throw new Error(`Presigning the S3 request failed with status ${status}`);

  return { url: data.url, key: data.key };
};

/**
 * The server owns the final key layout (year/month/uuid-filename) and returns
 * it from the first presign of an upload, so the client only hands over the
 * plain filename here.
 */
export const generateS3ObjectKey = (file: Pick<UppyFile<UppyMeta, AwsBody>, 'id' | 'name'>) =>
  file.name ?? file.id;
